import React, { useState, useEffect } from 'react';
import {
  PlayCircle, Plus, Trash2, Edit, Save, ArrowLeft, ArrowUp, ArrowDown,
  CheckCircle2, AlertCircle, Eye, EyeOff, Lock, Unlock, Sparkles,
  Layers, Filter, Search, Check, X, Sliders, ChevronDown, ChevronRight,
  HelpCircle, Monitor, Globe, User, HardDrive, FileText, Calendar,
  ListFilter, CheckSquare, Settings2, Play, RefreshCw, Info, Mail,
  Send, PhoneCall, ShieldCheck, Share2, CornerDownRight, Tag,
  Clock, Star, FileCode, MessageSquare, Link, Zap, ExternalLink, GitBranch,
  Users, Shield, UserCheck, AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '../../../lib/utils';
import { Dialog, DialogContent, DialogTitle } from '../../../components/Dialog';
import {
  useSupportSettingsStore,
  TicketActionConfig,
  SystemUseType,
  ActionFieldItem
} from '../../../store/supportSettingsStore';

export type { SystemUseType, ActionFieldItem, TicketActionConfig };

export const SYSTEM_USES_LIST: SystemUseType[] = [
  'None (Standard Action)',
  'Email User',
  'Email Agent',
  'Create Child Ticket',
  'Create Parent Ticket',
  'Create Related Ticket',
  'Create Opportunity',
  'Create Appointment/Task',
  'Complete Appointment/Task',
  'Add note to Child Ticket(s)',
  'Email Child Ticket(s)',
  'Add note to Parent Ticket',
  'Update Original Ticket',
  'Create Quotation',
  'Create Sales Order',
  'Create Purchase Order',
  'Create Invoice',
  'Re-assign',
  'Log to Supplier',
  'Email Supplier',
  'Supplier Response',
  'Supplier Fixed',
  'Create Bug/Issue (Jira/DevOps)',
  'Remote Session Invitation',
  'Create Knowledge Base Article',
  'Link To Knowledge Base Article',
  'Cancel Approval Process',
  'Email Accepted Approvers',
  'Email All Approvers',
  'Open URL',
  'Send Webhook/Queue Integration Runbook',
  'Consign Items',
  'Generate Report',
  'Create a new Asset',
  'Link to another Ticket',
  'Reclose',
  'Add CRM Note',
  'Save as a new Template',
  'Bulk email primary Service subscribers',
  'Forward',
  'Call on Microsoft Teams',
];

// Standard User / Agent definition for granular Access Control
interface GranularSubject {
  id: string;
  name: string;
  email: string;
  role: 'Super Admin' | 'HR Admin' | 'Reporting Manager' | 'Support Agent' | 'End User';
  category: 'admin' | 'agent' | 'manager' | 'user';
  department: string;
}

export function ActionsView() {
  const {
    ticketActions: storeActions,
    fetchTicketActions,
    saveTicketAction,
    deleteTicketAction,
    isLoadingTicketActions,
    ticketTypes: storeTicketTypes,
    fetchTicketTypes,
    extendedWorkflows: storeWorkflows,
    fetchExtendedWorkflows,
    agents: storeAgents,
    fetchAgents,
  } = useSupportSettingsStore();

  const [actions, setActions] = useState<TicketActionConfig[]>(storeActions || []);
  const [selectedActionId, setSelectedActionId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterGroup, setFilterGroup] = useState<string>('all');
  const [filterTicketType, setFilterTicketType] = useState<string>('all');
  const [viewingWiresAction, setViewingWiresAction] = useState<TicketActionConfig | null>(null);

  // Detail View State (Note: field_list removed as per user requirements)
  const [activeTab, setActiveTab] = useState<'details' | 'defaults' | 'other_uses' | 'access_control' | 'simulator'>('details');
  const isEditMode = selectedActionId !== null;
  const [originalSnapshot, setOriginalSnapshot] = useState<TicketActionConfig | null>(null);

  // Access Control Tab State
  const [accessSearchQuery, setAccessSearchQuery] = useState('');
  const [accessFilterRole, setAccessFilterRole] = useState<'all' | 'admin' | 'agent' | 'manager'>('all');

  // Simulator State (Live Execution Tester on Ticket)
  const [simTicketStatus, setSimTicketStatus] = useState('Open');
  const [simIsOnHold, setSimIsOnHold] = useState(false);
  const [simExecutedLogs, setSimExecutedLogs] = useState<string[]>([
    `[${new Date().toLocaleTimeString()}] Ticket TB#10042 loaded. Status: Open. SLA timer active.`
  ]);
  const [activeModalAction, setActiveModalAction] = useState<TicketActionConfig | null>(null);
  const [modalExecutionNote, setModalExecutionNote] = useState('');

  useEffect(() => {
    void fetchTicketActions();
    void fetchTicketTypes();
    void fetchExtendedWorkflows();
    void fetchAgents();
  }, [fetchTicketActions, fetchTicketTypes, fetchExtendedWorkflows, fetchAgents]);

  useEffect(() => {
    if (storeActions && storeActions.length > 0) {
      setActions(storeActions);
    }
  }, [storeActions]);

  const selectedAction = actions.find((a) => a.id === selectedActionId) || null;

  const granularRoster: GranularSubject[] = React.useMemo(() => storeAgents.map((agent) => ({
    id: agent.id,
    name: `${agent.first_name} ${agent.last_name}`,
    email: agent.email,
    role: 'Support Agent',
    category: 'agent',
    department: agent.team_name || agent.assigned_queues?.[0] || '',
  })), [storeAgents]);

  const handleSelectAction = (id: string) => {
    const act = actions.find((a) => a.id === id);
    setSelectedActionId(id);
    // Selecting an action opens its editable configuration in the right-side panel.
    setOriginalSnapshot(act ? JSON.parse(JSON.stringify(act)) : null);
    setActiveTab('details');
  };

  const handleBackToList = () => {
    if (selectedActionId) {
      setActions((current) => originalSnapshot
        ? current.map((action) => action.id === selectedActionId ? originalSnapshot : action)
        : current.filter((action) => action.id !== selectedActionId));
    }
    setSelectedActionId(null);
    setOriginalSnapshot(null);
  };

  // Apply & Save Handler
  const handleApplyAndSave = async () => {
    if (!selectedAction) return;

    if (!selectedAction.buttonName.trim()) {
      toast.error('Button Name is required');
      return;
    }
    if (!selectedAction.outcomeDescription.trim()) {
      toast.error('Outcome Description is required');
      return;
    }

    try {
      await saveTicketAction(selectedAction);
      setSelectedActionId(null);
      setOriginalSnapshot(null);
      toast.success(`Action Button "${selectedAction.buttonName}" applied & saved successfully!`);
    } catch {
      // Handled in store toast
    }
  };

  // Add New Action Button
  const handleCreateNewAction = () => {
    const newAct: TicketActionConfig = {
      id: `act-${Date.now()}`,
      outcomeDescription: 'New Custom Action Button',
      buttonName: 'Action Button',
      buttonIcon: 'PlayCircle',
      actionGroup: 'Communication',
      systemUse: 'None (Standard Action)',
      statusAfterAction: 'In Progress',
      isQuickAction: false,
      allowPortalUsers: false,
      showSlaHoldToggle: false,
      showHideFromEndUser: true,
      showMarkImportant: true,
      showFollowTicket: true,
      showCloseTicket: false,
      showTranslate: false,
      showSatisfactionSurvey: false,
      responseBehaviour: 'Default Behaviour',
      hideFromTicketHeader: false,
      visibilityOnParent: 'Show always',
      visibilityOnChild: 'Show always',
      sendEmail: false,
      suppressAutoResponseHeader: false,
      makeFromAddressVisible: false,
      defaultEmailTemplate: '',
      autoApplyTicketTemplate: '',
      defaultTeam: '',
      defaultAssignee: '',
      putOnSlaHold: false,
      releaseFromSlaHold: false,
      allowedTicketTypes: ['Incident', 'Service Request'],
      allowedWorkflows: ['General IT Support Workflow'],
      allowAdmins: true,
      allowAgents: true,
      allowManagers: true,
      allowedAgents: granularRoster.map((r) => r.id),
      accessControl: [
        { targetType: 'Role', targetName: 'Super Admin', level: 'Owner' },
        { targetType: 'Role', targetName: 'Support Agent', level: 'Execute Only' }
      ],
    };
    setActions([newAct, ...actions]);
    setSelectedActionId(newAct.id);
    setOriginalSnapshot(null);
    setActiveTab('details');
  };

  // Delete Action Button
  const handleDeleteSelectedAction = async () => {
    if (!selectedAction) return;
    const wireCount = selectedAction.wireCount || (selectedAction.wires ? selectedAction.wires.length : 0);
    if (wireCount > 0) {
      const confirmDeactivate = window.confirm(
        `Action Button "${selectedAction.buttonName}" is currently wired in ${wireCount} workflow transition wire(s).\n\nDeleting it would break these active workflows.\n\nWould you like to DEACTIVATE it instead?`
      );
      if (confirmDeactivate) {
        try {
          const updated = { ...selectedAction, isActive: false };
          await saveTicketAction(updated);
          setActions(actions.map((a) => (a.id === selectedAction.id ? updated : a)));
          setSelectedActionId(null);
          toast.success(`Action Button "${selectedAction.buttonName}" deactivated`);
        } catch {
          // Handled in store
        }
      }
      return;
    }

    if (!window.confirm(`Are you sure you want to delete Action Button "${selectedAction.buttonName}"?`)) return;
    try {
      await deleteTicketAction(selectedAction.id);
      setActions(actions.filter((a) => a.id !== selectedAction.id));
      setSelectedActionId(null);
      toast.success('Action Button deleted');
    } catch {
      // Store error
    }
  };

  // Toggle Ticket Type
  const handleToggleTicketType = (typeName: string) => {
    if (!selectedAction || !isEditMode) return;
    const current = selectedAction.allowedTicketTypes || [];
    const updated = current.includes(typeName)
      ? current.filter((t) => t !== typeName)
      : [...current, typeName];
    setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, allowedTicketTypes: updated } : a)));
  };

  // Toggle Workflow
  const handleToggleWorkflow = (wfName: string) => {
    if (!selectedAction || !isEditMode) return;
    const current = selectedAction.allowedWorkflows || [];
    const updated = current.includes(wfName)
      ? current.filter((w) => w !== wfName)
      : [...current, wfName];
    setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, allowedWorkflows: updated } : a)));
  };

  // Access Control Helpers: Granular user toggle
  const handleToggleGranularAccess = (subject: GranularSubject) => {
    if (!selectedAction || !isEditMode) return;
    const currentList = selectedAction.allowedAgents || [];
    const isCurrentlyAllowed = currentList.includes(subject.id);

    let updatedAllowed: string[];
    let updatedAccessControl = [...(selectedAction.accessControl || [])];

    if (isCurrentlyAllowed) {
      updatedAllowed = currentList.filter((id) => id !== subject.id);
      updatedAccessControl = updatedAccessControl.filter((ac) => ac.targetName !== subject.email && ac.targetName !== subject.name);
    } else {
      updatedAllowed = [...currentList, subject.id];
      const existingIdx = updatedAccessControl.findIndex((ac) => ac.targetName === subject.email || ac.targetName === subject.name);
      if (existingIdx >= 0) {
        updatedAccessControl[existingIdx].level = subject.category === 'admin' ? 'Owner' : 'Execute Only';
      } else {
        updatedAccessControl.push({
          targetType: subject.category === 'admin' ? 'Role' : 'Agent',
          targetName: subject.name,
          level: subject.category === 'admin' ? 'Owner' : 'Execute Only',
        });
      }
    }

    setActions(actions.map((a) => (a.id === selectedAction.id ? {
      ...a,
      allowedAgents: updatedAllowed,
      accessControl: updatedAccessControl
    } : a)));
  };

  // Change individual permission level
  const handleChangePermissionLevel = (subject: GranularSubject, level: 'Execute Only' | 'Read and Modify' | 'Owner') => {
    if (!selectedAction || !isEditMode) return;
    const updatedAccessControl = [...(selectedAction.accessControl || [])];
    const matchIdx = updatedAccessControl.findIndex((ac) => ac.targetName === subject.name || ac.targetName === subject.email);

    if (matchIdx >= 0) {
      updatedAccessControl[matchIdx] = { ...updatedAccessControl[matchIdx], level };
    } else {
      updatedAccessControl.push({
        targetType: subject.category === 'admin' ? 'Role' : 'Agent',
        targetName: subject.name,
        level,
      });
    }

    setActions(actions.map((a) => (a.id === selectedAction.id ? {
      ...a,
      accessControl: updatedAccessControl
    } : a)));
  };

  // Quick Preset Handlers for Access Control
  const handleAccessPreset = (preset: 'all' | 'admins_only' | 'staff_only' | 'reset') => {
    if (!selectedAction || !isEditMode) return;

    if (preset === 'all') {
      const allIds = granularRoster.map((r) => r.id);
      const allEntries = granularRoster.map((r) => ({
        targetType: (r.category === 'admin' ? 'Role' : 'Agent') as any,
        targetName: r.name,
        level: (r.category === 'admin' ? 'Owner' : 'Execute Only') as any,
      }));
      setActions(actions.map((a) => (a.id === selectedAction.id ? {
        ...a,
        allowAdmins: true,
        allowAgents: true,
        allowManagers: true,
        allowedAgents: allIds,
        accessControl: allEntries
      } : a)));
      toast.success('Granted access to all users, agents, and administrators');
    } else if (preset === 'admins_only') {
      const adminIds = granularRoster.filter((r) => r.category === 'admin').map((r) => r.id);
      const adminEntries = granularRoster.filter((r) => r.category === 'admin').map((r) => ({
        targetType: 'Role' as any,
        targetName: r.name,
        level: 'Owner' as any,
      }));
      setActions(actions.map((a) => (a.id === selectedAction.id ? {
        ...a,
        allowAdmins: true,
        allowAgents: false,
        allowManagers: false,
        allowPortalUsers: false,
        allowedAgents: adminIds,
        accessControl: adminEntries
      } : a)));
      toast.success('Restricted access to Administrators only');
    } else if (preset === 'staff_only') {
      const staffIds = granularRoster.filter((r) => r.category !== 'user').map((r) => r.id);
      setActions(actions.map((a) => (a.id === selectedAction.id ? {
        ...a,
        allowAdmins: true,
        allowAgents: true,
        allowManagers: true,
        allowPortalUsers: false,
        allowedAgents: staffIds,
      } : a)));
      toast.success('Restricted access to Internal Staff (Admins, Managers & Support Agents)');
    } else if (preset === 'reset') {
      setActions(actions.map((a) => (a.id === selectedAction.id ? {
        ...a,
        allowAdmins: true,
        allowAgents: true,
        allowManagers: true,
        allowedAgents: granularRoster.map((r) => r.id),
      } : a)));
      toast.success('Reset access control to default role settings');
    }
  };

  // Dynamically extract unique Action Groups / Categories from loaded database actions
  const availableGroups = React.useMemo(() => {
    const set = new Set<string>();
    actions.forEach((a) => {
      if (a.actionGroup) set.add(a.actionGroup);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [actions]);

  // Dynamically extract unique Ticket Types for filtering
  const availableTicketTypes = React.useMemo(() => {
    if (storeTicketTypes && storeTicketTypes.length > 0) {
      return storeTicketTypes.map((t) => t.name);
    }
    const set = new Set<string>();
    actions.forEach((a) => {
      (a.allowedTicketTypes || []).forEach((t) => {
        if (t && t !== '*') set.add(t);
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [storeTicketTypes, actions]);

  // Filtered Actions for Master Catalog
  const filteredActions = actions.filter((a) => {
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      a.buttonName.toLowerCase().includes(q) ||
      (a.code && a.code.toLowerCase().includes(q)) ||
      a.outcomeDescription.toLowerCase().includes(q) ||
      (a.systemUse && a.systemUse.toLowerCase().includes(q)) ||
      (a.actionGroup && a.actionGroup.toLowerCase().includes(q));
    const matchesGroup = filterGroup === 'all' || a.actionGroup === filterGroup;
    const matchesType =
      filterTicketType === 'all' ||
      (a.allowedTicketTypes && (a.allowedTicketTypes.includes('*') || a.allowedTicketTypes.includes(filterTicketType)));
    return matchesSearch && matchesGroup && matchesType;
  });

  // Simulator Execution Trigger
  const handleSimulateClickAction = (action: TicketActionConfig) => {
    if (action.isQuickAction) {
      const nextStatus = action.statusAfterAction || simTicketStatus;
      setSimTicketStatus(nextStatus);
      if (action.putOnSlaHold) setSimIsOnHold(true);
      if (action.releaseFromSlaHold) setSimIsOnHold(false);
      const log = `[${new Date().toLocaleTimeString()}] Quick Action Button "${action.buttonName}" executed! System Use: ${action.systemUse}. Ticket status updated to "${nextStatus}".`;
      setSimExecutedLogs((prev) => [log, ...prev]);
      toast.success(`Action Button "${action.buttonName}" executed!`);
    } else {
      setActiveModalAction(action);
      setModalExecutionNote('');
    }
  };

  const handleSimulateSubmitModal = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeModalAction) return;
    const nextStatus = activeModalAction.statusAfterAction || simTicketStatus;
    setSimTicketStatus(nextStatus);
    if (activeModalAction.putOnSlaHold) setSimIsOnHold(true);
    if (activeModalAction.releaseFromSlaHold) setSimIsOnHold(false);

    const log = `[${new Date().toLocaleTimeString()}] Action Button "${activeModalAction.buttonName}" applied by Agent. Note: "${modalExecutionNote || 'None'}". Outcome: "${activeModalAction.outcomeDescription}". Status changed to "${nextStatus}" (SLA Hold: ${simIsOnHold ? 'ON' : 'OFF'}).`;
    setSimExecutedLogs((prev) => [log, ...prev]);
    toast.success(`Action Button "${activeModalAction.buttonName}" applied to ticket!`);
    setActiveModalAction(null);
  };

  return (
    <div className="space-y-6 text-foreground text-xs font-sans animate-in fade-in-50 duration-150">
      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* 1. MASTER LIST: ACTION BUTTONS CATALOG */}
      {/* ═════════════════════════════════════════════════════════════════ */}
        <div className="space-y-5">
          <div className="rounded-xl border border-border bg-card p-4 shadow-xs space-y-4">
            {/* Filter, Search and Actions Bar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 w-full flex-wrap">
                <div className="relative flex-1 max-w-sm min-w-[200px]">
                  <Search className="size-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search action buttons by button name, outcome, or system use..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 bg-background border border-input rounded-lg text-xs outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground font-medium">Category:</span>
                  <select
                    value={filterGroup}
                    onChange={(e) => setFilterGroup(e.target.value)}
                    className="h-8 rounded-lg border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring cursor-pointer min-w-[150px]"
                  >
                    <option value="all">All Categories ({actions.length})</option>
                    {availableGroups.map((grp) => {
                      const count = actions.filter((a) => a.actionGroup === grp).length;
                      return (
                        <option key={grp} value={grp}>
                          {grp} ({count})
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground font-medium">Ticket Type:</span>
                  <select
                    value={filterTicketType}
                    onChange={(e) => setFilterTicketType(e.target.value)}
                    className="h-8 rounded-lg border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring cursor-pointer min-w-[150px]"
                  >
                    <option value="all">All Ticket Types</option>
                    {availableTicketTypes.map((tt) => (
                      <option key={tt} value={tt}>
                        {tt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                <button
                  type="button"
                  onClick={() => setActiveTab('simulator')}
                  className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border bg-card px-3 text-xs font-semibold text-foreground hover:bg-secondary transition-colors cursor-pointer"
                >
                  <Play className="size-3 text-primary" /> Simulator
                </button>

                <button type="button" onClick={handleCreateNewAction} className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-primary px-3 text-xs font-semibold text-primary-foreground hover:bg-primary/90 shadow-xs cursor-pointer">
                  <Plus className="size-3.5" /> New action button
                </button>
              </div>
            </div>

            {/* Action Buttons Data Table */}
            <div className="rounded-xl border border-border bg-card overflow-hidden shadow-xs">
              {filteredActions.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <PlayCircle className="size-8 mx-auto mb-2 opacity-30 text-primary" />
                  <p className="font-semibold text-xs text-foreground">No Action Buttons found</p>
                  <p className="text-xs mt-0.5">Try adjusting your search criteria or create a new action button.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-secondary/40 border-b border-border text-muted-foreground font-semibold uppercase text-[10px] tracking-wider whitespace-nowrap">
                      <tr>
                        <th className="py-2.5 px-4">Action Button</th>
                        <th className="py-2.5 px-4">Outcome Description</th>
                        <th className="py-2.5 px-4">Category</th>
                        <th className="py-2.5 px-4">Used in</th>
                        <th className="py-2.5 px-4">Code / System Use</th>
                        <th className="py-2.5 px-4">Allowed Access</th>
                        <th className="py-2.5 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredActions.map((act) => {
                        const roles = [
                          act.allowAdmins !== false && 'Admins',
                          act.allowAgents !== false && 'Agents',
                          act.allowManagers !== false && 'Managers',
                        ].filter(Boolean).join(', ');

                        const wireCount = act.wireCount ?? (act.wires ? act.wires.length : 0);

                        return (
                          <tr
                            key={act.id}
                            onClick={() => handleSelectAction(act.id)}
                            className="hover:bg-secondary/30 transition-colors cursor-pointer group"
                          >
                            <td className="py-2.5 px-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-0.5 rounded-md border border-border bg-secondary font-mono text-xs font-semibold text-foreground flex items-center gap-1.5 shadow-2xs">
                                  <PlayCircle className="size-3 text-primary shrink-0" />
                                  <span>{act.buttonName}</span>
                                </span>
                                {act.isQuickAction && (
                                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-warning/10 text-warning border border-warning/20 whitespace-nowrap">
                                    Quick
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-2.5 px-4 text-foreground font-medium max-w-xs truncate">
                              <span title={act.outcomeDescription} className="group-hover:text-primary transition-colors">
                                {act.outcomeDescription || '—'}
                              </span>
                            </td>
                            <td className="py-2.5 px-4 whitespace-nowrap">
                              {act.actionGroup ? (
                                <span className={cn(
                                  "text-[10px] font-semibold px-2 py-0.5 rounded border inline-flex items-center",
                                  act.actionGroup.toUpperCase().includes('PRIMARY')
                                    ? "bg-primary/10 text-primary border-primary/20"
                                    : "bg-secondary text-muted-foreground border-border/60"
                                )}>
                                  {act.actionGroup.toUpperCase().includes('PRIMARY') ? 'Primary' : act.actionGroup}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="py-2.5 px-4 whitespace-nowrap">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setViewingWiresAction(act);
                                }}
                                className={cn(
                                  "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border transition-colors cursor-pointer",
                                  wireCount > 0
                                    ? "bg-primary/10 text-primary border-primary/20 hover:bg-primary/20"
                                    : "bg-secondary text-muted-foreground border-border/60 hover:bg-secondary/80"
                                )}
                                title="Click to view workflow transition wires"
                              >
                                <GitBranch className="size-3" />
                                <span>{wireCount} wire{wireCount === 1 ? '' : 's'}</span>
                              </button>
                            </td>
                            <td className="py-2.5 px-4 whitespace-nowrap">
                              <div className="flex items-center gap-1.5">
                                <code className="font-mono text-[11px] text-foreground bg-secondary/70 px-1.5 py-0.5 rounded border border-border/60">
                                  {act.code || act.systemUse}
                                </code>
                                {act.statusAfterAction && (
                                  <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                                    <CornerDownRight className="size-2.5 text-primary" /> {act.statusAfterAction}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-2.5 px-4 whitespace-nowrap text-muted-foreground">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs">{roles || 'All Staff'}</span>
                                {act.allowPortalUsers && (
                                  <span className="text-[10px] font-medium px-1.5 py-0.2 rounded bg-info/10 text-info border border-info/20">
                                    Portal
                                  </span>
                                )}
                                {act.sendEmail && (
                                  <span className="text-[10px] font-medium px-1.5 py-0.2 rounded bg-success/10 text-success border border-success/20 flex items-center gap-0.5" title="Sends notification email">
                                    <Mail className="size-2.5" />
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-2.5 px-4 text-right whitespace-nowrap">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectAction(act.id);
                                }}
                                className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-xs font-medium text-foreground hover:bg-secondary hover:text-foreground transition-colors shadow-2xs"
                                title="Edit action button"
                              >
                                <Edit className="size-3" />
                                <span>Edit</span>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      {selectedAction && (
        <Dialog open onOpenChange={(open) => { if (!open) handleBackToList(); }}>
        <DialogContent side="right" className="max-w-2xl gap-4 p-5" aria-describedby={undefined}>
          <div className="space-y-3 border-b border-border pb-3 pr-8">
            <DialogTitle>Edit {selectedAction.buttonName}</DialogTitle>
            <div className="flex items-center gap-2 overflow-x-auto">
              {([{ id: 'details', label: 'Details' }, { id: 'defaults', label: 'Defaults' }, { id: 'other_uses', label: 'Other uses' }, { id: 'access_control', label: 'Access control' }] as const).map((tab) => (
                <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={cn('h-9 whitespace-nowrap border-b-2 px-3 text-sm font-medium', activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground')}>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'details' && (
            <div className="rounded-xl border border-border bg-card p-5 shadow-xs space-y-5">
              <div className="border-b border-border pb-3">
                <h3 className="font-display text-card-title text-foreground">Action Button Details &amp; Core Behaviors</h3>
                <p className="text-xs text-muted-foreground">Define button labeling, icon, system functionality, and ticket state transitions.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="font-semibold text-foreground flex items-center justify-between mb-1">
                    <span>Action Code *</span>
                    {!selectedAction.id.startsWith('act-') && (
                      <span className="text-[10px] text-muted-foreground flex items-center gap-1 font-normal">
                        <Lock className="size-3" /> Read-only
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={selectedAction.code || selectedAction.systemUse || ''}
                    disabled={!isEditMode || !selectedAction.id.startsWith('act-')}
                    readOnly={!selectedAction.id.startsWith('act-')}
                    onChange={(e) => {
                      if (selectedAction.id.startsWith('act-')) {
                        const code = e.target.value.toUpperCase().replace(/[^A-Z0-9_]+/g, '_');
                        setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, code, systemUse: code } : a)));
                      }
                    }}
                    placeholder="e.g. SEND_ACKNOWLEDGEMENT"
                    className="h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-muted/30"
                  />
                  <p className="text-[11px] text-muted-foreground mt-0.5">ITSM workflow engine code.</p>
                </div>

                <div>
                  <label className="font-semibold text-foreground block mb-1">Outcome Description *</label>
                  <input
                    type="text"
                    value={selectedAction.outcomeDescription}
                    disabled={!isEditMode}
                    onChange={(e) => {
                      const outcomeDescription = e.target.value;
                      setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, outcomeDescription } : a)));
                    }}
                    className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                  />
                  <p className="text-[11px] text-muted-foreground mt-0.5">Timeline &amp; audit label.</p>
                </div>

                <div>
                  <label className="font-semibold text-foreground block mb-1">Button Name *</label>
                  <input
                    type="text"
                    value={selectedAction.buttonName}
                    disabled={!isEditMode}
                    onChange={(e) => {
                      const buttonName = e.target.value;
                      setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, buttonName } : a)));
                    }}
                    className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground font-semibold outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                  />
                  <p className="text-[11px] text-muted-foreground mt-0.5">Top toolbar button label.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
                <div>
                  <label className="font-semibold text-foreground block mb-1">Action Group</label>
                  <select
                    value={selectedAction.actionGroup}
                    disabled={!isEditMode}
                    onChange={(e) => {
                      const actionGroup = e.target.value as any;
                      setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, actionGroup } : a)));
                    }}
                    className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <option value="Communication">Communication</option>
                    <option value="Hierarchy &amp; Linking">Hierarchy &amp; Linking</option>
                    <option value="Escalation &amp; Reassign">Escalation &amp; Reassign</option>
                    <option value="Finance &amp; Orders">Finance &amp; Orders</option>
                    <option value="External &amp; Automation">External &amp; Automation</option>
                    <option value="Resolution &amp; Closure">Resolution &amp; Closure</option>
                  </select>
                </div>

                <div>
                  <label className="font-semibold text-foreground block mb-1">System Use (Preconfigured Function)</label>
                  <select
                    value={selectedAction.systemUse}
                    disabled={!isEditMode}
                    onChange={(e) => {
                      const systemUse = e.target.value as any;
                      setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, systemUse } : a)));
                    }}
                    className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs font-semibold text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {SYSTEM_USES_LIST.map((su) => (
                      <option key={su} value={su}>{su}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="font-semibold text-foreground block mb-1">Status After Action</label>
                  <select
                    value={selectedAction.statusAfterAction}
                    disabled={!isEditMode}
                    onChange={(e) => {
                      const statusAfterAction = e.target.value;
                      setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, statusAfterAction } : a)));
                    }}
                    className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <option value="No Change">No Change</option>
                    <option value="Open">Open</option>
                    <option value="In Progress">In Progress</option>
                    <option value="With User">With User (Awaiting Response)</option>
                    <option value="Pending Vendor">Pending Vendor</option>
                    <option value="Pending Approval">Pending Approval</option>
                    <option value="Resolved">Resolved</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>
              </div>

              {/* Quick Action & Portal Access */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <label className={cn("flex items-center gap-2 text-xs font-semibold text-foreground p-3 rounded-lg border border-border bg-muted/20", isEditMode ? "cursor-pointer" : "cursor-default opacity-80")}>
                  <input
                    type="checkbox"
                    checked={selectedAction.isQuickAction}
                    disabled={!isEditMode}
                    onChange={(e) => {
                      const isQuickAction = e.target.checked;
                      setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, isQuickAction } : a)));
                    }}
                    className="size-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <div>
                    <span className="font-semibold block">Is a Quick Action</span>
                    <span className="text-xs text-muted-foreground font-normal">When enabled, triggers automatically upon button click without opening intermediate dialogs.</span>
                  </div>
                </label>

                <label className={cn("flex items-center gap-2 text-xs font-semibold text-foreground p-3 rounded-lg border border-border bg-muted/20", isEditMode ? "cursor-pointer" : "cursor-default opacity-80")}>
                  <input
                    type="checkbox"
                    checked={selectedAction.allowPortalUsers}
                    disabled={!isEditMode}
                    onChange={(e) => {
                      const allowPortalUsers = e.target.checked;
                      setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, allowPortalUsers } : a)));
                    }}
                    className="size-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <div>
                    <span className="font-semibold block">Allow Users to use this action button</span>
                    <span className="text-xs text-muted-foreground font-normal">Enables end-users of a ticket to invoke this action button from the Self-Service Portal.</span>
                  </div>
                </label>
              </div>

              {/* "Show the" Options on Action Form */}
              <div className="p-4 rounded-xl border border-border bg-muted/20 space-y-3">
                <h4 className="font-semibold text-xs text-foreground uppercase tracking-wider flex items-center gap-2">
                  <Eye className="size-3.5 text-primary" /> "Show the" Options (Toolbar Controls on Action Execution)
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <label className={cn("flex items-center gap-2 text-xs text-foreground", isEditMode ? "cursor-pointer" : "cursor-default opacity-80")}>
                    <input
                      type="checkbox"
                      checked={selectedAction.showSlaHoldToggle}
                      disabled={!isEditMode}
                      onChange={(e) => {
                        const showSlaHoldToggle = e.target.checked;
                        setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, showSlaHoldToggle } : a)));
                      }}
                      className="size-3.5 rounded border-border text-primary"
                    />
                    <span>SLA Hold Toggle (Play / Pause)</span>
                  </label>

                  <label className={cn("flex items-center gap-2 text-xs text-foreground", isEditMode ? "cursor-pointer" : "cursor-default opacity-80")}>
                    <input
                      type="checkbox"
                      checked={selectedAction.showHideFromEndUser}
                      disabled={!isEditMode}
                      onChange={(e) => {
                        const showHideFromEndUser = e.target.checked;
                        setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, showHideFromEndUser } : a)));
                      }}
                      className="size-3.5 rounded border-border text-primary"
                    />
                    <span>Hide from End-User (Private Eye)</span>
                  </label>

                  <label className={cn("flex items-center gap-2 text-xs text-foreground", isEditMode ? "cursor-pointer" : "cursor-default opacity-80")}>
                    <input
                      type="checkbox"
                      checked={selectedAction.showMarkImportant}
                      disabled={!isEditMode}
                      onChange={(e) => {
                        const showMarkImportant = e.target.checked;
                        setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, showMarkImportant } : a)));
                      }}
                      className="size-3.5 rounded border-border text-primary"
                    />
                    <span>Mark as Important (Star)</span>
                  </label>

                  <label className={cn("flex items-center gap-2 text-xs text-foreground", isEditMode ? "cursor-pointer" : "cursor-default opacity-80")}>
                    <input
                      type="checkbox"
                      checked={selectedAction.showFollowTicket}
                      disabled={!isEditMode}
                      onChange={(e) => {
                        const showFollowTicket = e.target.checked;
                        setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, showFollowTicket } : a)));
                      }}
                      className="size-3.5 rounded border-border text-primary"
                    />
                    <span>Follow this Ticket (Eye)</span>
                  </label>

                  <label className={cn("flex items-center gap-2 text-xs text-foreground", isEditMode ? "cursor-pointer" : "cursor-default opacity-80")}>
                    <input
                      type="checkbox"
                      checked={selectedAction.showCloseTicket}
                      disabled={!isEditMode}
                      onChange={(e) => {
                        const showCloseTicket = e.target.checked;
                        setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, showCloseTicket } : a)));
                      }}
                      className="size-3.5 rounded border-border text-primary"
                    />
                    <span>Close this Ticket (Tick)</span>
                  </label>

                  <label className={cn("flex items-center gap-2 text-xs text-foreground", isEditMode ? "cursor-pointer" : "cursor-default opacity-80")}>
                    <input
                      type="checkbox"
                      checked={selectedAction.showSatisfactionSurvey}
                      disabled={!isEditMode}
                      onChange={(e) => {
                        const showSatisfactionSurvey = e.target.checked;
                        setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, showSatisfactionSurvey } : a)));
                      }}
                      className="size-3.5 rounded border-border text-primary"
                    />
                    <span>Send Satisfaction Survey (Pen)</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* TAB 2: DEFAULTS TAB */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {activeTab === 'defaults' && (
            <div className="rounded-xl border border-border bg-card p-5 shadow-xs space-y-5">
              <div className="border-b border-border pb-3">
                <h3 className="font-display text-card-title text-foreground">Action Button Defaults &amp; Automation Targets</h3>
                <p className="text-xs text-muted-foreground">Configure default email dispatching, SLA hold policies, templates, and team reassignments.</p>
              </div>

              {/* Email Section */}
              <div className="p-4 rounded-xl border border-border bg-muted/20 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-xs text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Mail className="size-3.5 text-primary" /> Automated Email Notifications
                  </h4>
                  <label className={cn("flex items-center gap-1.5 text-xs font-semibold", isEditMode ? "cursor-pointer text-foreground" : "cursor-default opacity-80")}>
                    <input
                      type="checkbox"
                      checked={selectedAction.sendEmail}
                      disabled={!isEditMode}
                      onChange={(e) => {
                        const sendEmail = e.target.checked;
                        setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, sendEmail } : a)));
                      }}
                      className="size-4 rounded border-border text-primary"
                    />
                    <span>Send Email when this Action Button is clicked</span>
                  </label>
                </div>

                {selectedAction.sendEmail && (
                  <div className="space-y-3 pt-2 border-t border-border/60">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="font-semibold text-foreground block mb-1">Default Email Template</label>
                        <select
                          value={selectedAction.defaultEmailTemplate}
                          disabled={!isEditMode}
                          onChange={(e) => {
                            const defaultEmailTemplate = e.target.value;
                            setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, defaultEmailTemplate } : a)));
                          }}
                          className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <option value="">(No specific template - blank editor)</option>
                          <option value="Customer Ticket Update Notification">Customer Ticket Update Notification</option>
                          <option value="Escalation Notice to Level 2">Escalation Notice to Level 2</option>
                          <option value="Resolution Confirmation &amp; Survey">Resolution Confirmation &amp; Survey</option>
                          <option value="Supplier Query Dispatch">Supplier Query Dispatch</option>
                        </select>
                      </div>

                      <div className="flex flex-col justify-end gap-2">
                        <label className={cn("flex items-center gap-2 text-xs text-foreground", isEditMode ? "cursor-pointer" : "cursor-default opacity-80")}>
                          <input
                            type="checkbox"
                            checked={selectedAction.suppressAutoResponseHeader}
                            disabled={!isEditMode}
                            onChange={(e) => {
                              const suppressAutoResponseHeader = e.target.checked;
                              setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, suppressAutoResponseHeader } : a)));
                            }}
                            className="size-3.5 rounded border-border text-primary"
                          />
                          <span>Suppress Auto-Response Header (Precedence: bulk)</span>
                        </label>

                        <label className={cn("flex items-center gap-2 text-xs text-foreground", isEditMode ? "cursor-pointer" : "cursor-default opacity-80")}>
                          <input
                            type="checkbox"
                            checked={selectedAction.makeFromAddressVisible}
                            disabled={!isEditMode}
                            onChange={(e) => {
                              const makeFromAddressVisible = e.target.checked;
                              setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, makeFromAddressVisible } : a)));
                            }}
                            className="size-3.5 rounded border-border text-primary"
                          />
                          <span>Make From Address visible and editable by agent</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* SLA & Hold Policies */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl border border-border bg-muted/20 space-y-3">
                  <h4 className="font-semibold text-xs text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Clock className="size-3.5 text-primary" /> SLA Target Timers
                  </h4>

                  <div className="space-y-2">
                    <label className={cn("flex items-center gap-2 text-xs text-foreground", isEditMode ? "cursor-pointer" : "cursor-default opacity-80")}>
                      <input
                        type="checkbox"
                        checked={selectedAction.putOnSlaHold}
                        disabled={!isEditMode}
                        onChange={(e) => {
                          const putOnSlaHold = e.target.checked;
                          setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, putOnSlaHold } : a)));
                        }}
                        className="size-4 rounded border-border text-amber-500"
                      />
                      <div>
                        <span className="font-semibold block">Put Ticket on SLA Hold</span>
                        <span className="text-xs text-muted-foreground font-normal">Freezes the resolution countdown clock until next response.</span>
                      </div>
                    </label>

                    <label className={cn("flex items-center gap-2 text-xs text-foreground", isEditMode ? "cursor-pointer" : "cursor-default opacity-80")}>
                      <input
                        type="checkbox"
                        checked={selectedAction.releaseFromSlaHold}
                        disabled={!isEditMode}
                        onChange={(e) => {
                          const releaseFromSlaHold = e.target.checked;
                          setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, releaseFromSlaHold } : a)));
                        }}
                        className="size-4 rounded border-border text-emerald-500"
                      />
                      <div>
                        <span className="font-semibold block">Release from SLA Hold</span>
                        <span className="text-xs text-muted-foreground font-normal">Resumes SLA tracking if the ticket was previously paused.</span>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="p-4 rounded-xl border border-border bg-muted/20 space-y-3">
                  <h4 className="font-semibold text-xs text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Zap className="size-3.5 text-primary" /> Response Behaviour
                  </h4>

                  <div>
                    <label className="font-semibold text-foreground block mb-1">Impact on First Response SLA</label>
                    <select
                      value={selectedAction.responseBehaviour}
                      disabled={!isEditMode}
                      onChange={(e) => {
                        const responseBehaviour = e.target.value as any;
                        setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, responseBehaviour } : a)));
                      }}
                      className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <option value="Default Behaviour">Default Behaviour (Check for user contact)</option>
                      <option value="Mark Responded">Mark Responded (Complete 1st Response SLA)</option>
                      <option value="Do not affect SLA">Do not affect SLA (Internal step)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Assignment Defaults */}
              <div className="p-4 rounded-xl border border-border bg-muted/20 space-y-3">
                <h4 className="font-semibold text-xs text-foreground uppercase tracking-wider flex items-center gap-2">
                  <Users className="size-3.5 text-primary" /> Automated Reassignment Defaults
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-semibold text-foreground block mb-1">Default Assignee</label>
                    <select
                      value={selectedAction.defaultAssignee}
                      disabled={!isEditMode}
                      onChange={(e) => {
                        const defaultAssignee = e.target.value;
                        setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, defaultAssignee } : a)));
                      }}
                      className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <option value="">(Keep existing ticket assignee)</option>
                      {storeAgents && storeAgents.map((ag) => (
                        <option key={ag.id} value={`${ag.first_name} ${ag.last_name}`}>
                          {ag.first_name} {ag.last_name} ({ag.role || 'Agent'})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="font-semibold text-foreground block mb-1">Default Support Team</label>
                    <input
                      type="text"
                      value={selectedAction.defaultTeam}
                      placeholder="e.g. Infrastructure &amp; DevOps"
                      disabled={!isEditMode}
                      onChange={(e) => {
                        const defaultTeam = e.target.value;
                        setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, defaultTeam } : a)));
                      }}
                      className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* TAB 3: OTHER USES TAB */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {activeTab === 'other_uses' && (() => {
            const availableTicketTypes = storeTicketTypes && storeTicketTypes.length > 0
              ? storeTicketTypes.map((t) => t.name)
              : ['Incident', 'Service Request', 'Problem', 'Change Request', 'Bug Report', 'Feature Request', 'Question / Inquiry'];

            const availableWorkflows = storeWorkflows && storeWorkflows.length > 0
              ? storeWorkflows.map((w) => w.name)
              : ['Incident Lifecycle Workflow', 'Service Request Lifecycle Workflow', 'Problem Lifecycle Workflow', 'Change Request Lifecycle Workflow', 'General IT Support Workflow'];

            return (
              <div className="rounded-xl border border-border bg-card p-5 shadow-xs space-y-4">
                <div className="border-b border-border pb-3">
                  <h3 className="font-display text-card-title text-foreground">Workflow &amp; Ticket Type References</h3>
                  <p className="text-xs text-muted-foreground">Areas where "{selectedAction.buttonName}" is permitted and active across your ITSM workspace.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl border border-border bg-muted/20 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-xs text-foreground uppercase tracking-wider flex items-center gap-1.5">
                        <Tag className="size-3.5 text-primary" /> Allowed Ticket Types ({(selectedAction.allowedTicketTypes || []).length})
                      </h4>
                      {isEditMode && <span className="text-xs text-muted-foreground">Check to enable</span>}
                    </div>

                    <div className="space-y-1 text-xs">
                      {availableTicketTypes.map((tt) => {
                        const isAllowed = (selectedAction.allowedTicketTypes || []).includes(tt);
                        if (!isEditMode && !isAllowed) return null;
                        return (
                          <label key={tt} className={cn("flex items-center gap-2 p-1.5 rounded-lg transition-colors", isEditMode ? "hover:bg-muted/50 cursor-pointer" : "cursor-default")}>
                            {isEditMode ? (
                              <input
                                type="checkbox"
                                checked={isAllowed}
                                onChange={() => handleToggleTicketType(tt)}
                                className="size-4 rounded border-border text-primary focus:ring-primary"
                              />
                            ) : (
                              <Check className="size-3 text-emerald-500 shrink-0" />
                            )}
                            <span className={cn("text-xs font-semibold", isAllowed ? "text-foreground" : "text-muted-foreground")}>
                              {tt}
                            </span>
                          </label>
                        );
                      })}
                      {!isEditMode && (selectedAction.allowedTicketTypes || []).length === 0 && (
                        <p className="text-xs text-muted-foreground italic">None selected (Not visible on tickets)</p>
                      )}
                    </div>
                  </div>

                  <div className="p-4 rounded-xl border border-border bg-muted/20 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-xs text-foreground uppercase tracking-wider flex items-center gap-1.5">
                        <GitBranch className="size-3.5 text-purple-600" /> Allowed Workflows ({(selectedAction.allowedWorkflows || []).length})
                      </h4>
                      {isEditMode && <span className="text-xs text-muted-foreground">Check to enable</span>}
                    </div>

                    <div className="space-y-1 text-xs">
                      {availableWorkflows.map((wf) => {
                        const isAllowed = (selectedAction.allowedWorkflows || []).includes(wf);
                        if (!isEditMode && !isAllowed) return null;
                        return (
                          <label key={wf} className={cn("flex items-center gap-2 p-1.5 rounded-lg transition-colors", isEditMode ? "hover:bg-muted/50 cursor-pointer" : "cursor-default")}>
                            {isEditMode ? (
                              <input
                                type="checkbox"
                                checked={isAllowed}
                                onChange={() => handleToggleWorkflow(wf)}
                                className="size-4 rounded border-border text-primary focus:ring-primary"
                              />
                            ) : (
                              <Check className="size-3 text-emerald-500 shrink-0" />
                            )}
                            <span className={cn("text-xs font-semibold", isAllowed ? "text-foreground" : "text-muted-foreground")}>
                              {wf}
                            </span>
                          </label>
                        );
                      })}
                      {!isEditMode && (selectedAction.allowedWorkflows || []).length === 0 && (
                        <p className="text-xs text-muted-foreground italic">None selected (Available universally)</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* TAB 4: ACCESS CONTROL TAB (IMPLEMENTED BY USERS / AGENT / ADMIN) */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {activeTab === 'access_control' && (
            <div className="rounded-xl border border-border bg-card p-5 shadow-xs space-y-6">
              <div className="border-b border-border pb-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
                    <ShieldCheck className="size-4 text-primary" /> Role &amp; Granular User Access Control
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Configure visibility and execution permissions for this Action Button across Administrators, Support Agents, Reporting Managers, and End Users.
                  </p>
                </div>

                {isEditMode && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button
                      type="button"
                      onClick={() => handleAccessPreset('all')}
                      className="px-2.5 py-1 text-xs font-semibold rounded-md border border-border bg-background hover:bg-accent text-foreground transition-colors cursor-pointer"
                    >
                      Grant All
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAccessPreset('admins_only')}
                      className="px-2.5 py-1 text-xs font-semibold rounded-md border border-border bg-background hover:bg-accent text-foreground transition-colors cursor-pointer"
                    >
                      Admins Only
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAccessPreset('staff_only')}
                      className="px-2.5 py-1 text-xs font-semibold rounded-md border border-border bg-background hover:bg-accent text-foreground transition-colors cursor-pointer"
                    >
                      All Staff
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAccessPreset('reset')}
                      className="px-2.5 py-1 text-xs font-semibold rounded-md border border-border bg-background hover:bg-accent text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                    >
                      Reset
                    </button>
                  </div>
                )}
              </div>

              {/* 1. High-Level Role Toggles Matrix */}
              <div className="space-y-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block">
                  1. Role Access Permission Matrix
                </span>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
                  {/* System Admins */}
                  <div className={cn(
                    "p-3.5 rounded-xl border transition-all",
                    selectedAction.allowAdmins !== false ? "border-primary/40 bg-primary/5" : "border-border bg-muted/20 opacity-70"
                  )}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-primary/10 text-primary">
                          <Shield className="size-4" />
                        </div>
                        <div>
                          <span className="font-semibold text-xs text-foreground block">Administrators</span>
                          <span className="text-xs text-muted-foreground font-mono">Full Access</span>
                        </div>
                      </div>

                      {isEditMode ? (
                        <input
                          type="checkbox"
                          checked={selectedAction.allowAdmins !== false}
                          onChange={(e) => {
                            const allowAdmins = e.target.checked;
                            setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, allowAdmins } : a)));
                          }}
                          className="size-4 rounded border-border text-primary focus:ring-primary cursor-pointer mt-0.5"
                        />
                      ) : (
                        <span className={cn(
                          "text-xs font-semibold px-2 py-0.5 rounded-full uppercase",
                          selectedAction.allowAdmins !== false ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300" : "bg-muted text-muted-foreground"
                        )}>
                          {selectedAction.allowAdmins !== false ? 'Active' : 'Disabled'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Super Admins and System Admins can view, edit, configure, and execute this action button.
                    </p>
                  </div>

                  {/* Support Agents */}
                  <div className={cn(
                    "p-3.5 rounded-xl border transition-all",
                    selectedAction.allowAgents !== false ? "border-blue-500/40 bg-blue-500/5" : "border-border bg-muted/20 opacity-70"
                  )}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400">
                          <Users className="size-4" />
                        </div>
                        <div>
                          <span className="font-semibold text-xs text-foreground block">Support Agents</span>
                          <span className="text-xs text-muted-foreground font-mono">Toolbar Execution</span>
                        </div>
                      </div>

                      {isEditMode ? (
                        <input
                          type="checkbox"
                          checked={selectedAction.allowAgents !== false}
                          onChange={(e) => {
                            const allowAgents = e.target.checked;
                            setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, allowAgents } : a)));
                          }}
                          className="size-4 rounded border-border text-primary focus:ring-primary cursor-pointer mt-0.5"
                        />
                      ) : (
                        <span className={cn(
                          "text-xs font-semibold px-2 py-0.5 rounded-full uppercase",
                          selectedAction.allowAgents !== false ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300" : "bg-muted text-muted-foreground"
                        )}>
                          {selectedAction.allowAgents !== false ? 'Active' : 'Disabled'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Support agents can execute this button on tickets assigned to their queues.
                    </p>
                  </div>

                  {/* Reporting Managers */}
                  <div className={cn(
                    "p-3.5 rounded-xl border transition-all",
                    selectedAction.allowManagers !== false ? "border-purple-500/40 bg-purple-500/5" : "border-border bg-muted/20 opacity-70"
                  )}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-purple-500/10 text-purple-600 dark:text-purple-400">
                          <UserCheck className="size-4" />
                        </div>
                        <div>
                          <span className="font-semibold text-xs text-foreground block">Reporting Managers</span>
                          <span className="text-xs text-muted-foreground font-mono">Approval &amp; Actions</span>
                        </div>
                      </div>

                      {isEditMode ? (
                        <input
                          type="checkbox"
                          checked={selectedAction.allowManagers !== false}
                          onChange={(e) => {
                            const allowManagers = e.target.checked;
                            setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, allowManagers } : a)));
                          }}
                          className="size-4 rounded border-border text-primary focus:ring-primary cursor-pointer mt-0.5"
                        />
                      ) : (
                        <span className={cn(
                          "text-xs font-semibold px-2 py-0.5 rounded-full uppercase",
                          selectedAction.allowManagers !== false ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300" : "bg-muted text-muted-foreground"
                        )}>
                          {selectedAction.allowManagers !== false ? 'Active' : 'Disabled'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Department supervisors and team leads can trigger and oversee action outcomes.
                    </p>
                  </div>

                  {/* Customer Portal End Users */}
                  <div className={cn(
                    "p-3.5 rounded-xl border transition-all",
                    selectedAction.allowPortalUsers ? "border-emerald-500/40 bg-emerald-500/5" : "border-border bg-muted/20 opacity-70"
                  )}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                          <Globe className="size-4" />
                        </div>
                        <div>
                          <span className="font-semibold text-xs text-foreground block">Customer End Users</span>
                          <span className="text-xs text-muted-foreground font-mono">Self-Service Portal</span>
                        </div>
                      </div>

                      {isEditMode ? (
                        <input
                          type="checkbox"
                          checked={selectedAction.allowPortalUsers}
                          onChange={(e) => {
                            const allowPortalUsers = e.target.checked;
                            setActions(actions.map((a) => (a.id === selectedAction.id ? { ...a, allowPortalUsers } : a)));
                          }}
                          className="size-4 rounded border-border text-primary focus:ring-primary cursor-pointer mt-0.5"
                        />
                      ) : (
                        <span className={cn(
                          "text-xs font-semibold px-2 py-0.5 rounded-full uppercase",
                          selectedAction.allowPortalUsers ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300" : "bg-muted text-muted-foreground"
                        )}>
                          {selectedAction.allowPortalUsers ? 'Active' : 'Disabled'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Requesters and clients can click this Action Button directly from the support portal.
                    </p>
                  </div>
                </div>
              </div>

              {/* 2. Granular User / Agent / Admin Table */}
              <div className="space-y-3 pt-2">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block">
                      2. Granular Access by Each User / Agent / Administrator
                    </span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Grant individual permissions or override role defaults for specific agents and administrators.
                    </p>
                  </div>

                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 sm:w-56">
                      <Search className="size-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        placeholder="Search user, agent, admin..."
                        value={accessSearchQuery}
                        onChange={(e) => setAccessSearchQuery(e.target.value)}
                        className="w-full pl-8 pr-2.5 py-1 bg-background border border-input rounded-md text-xs outline-none focus:ring-1 focus:ring-ring"
                      />
                    </div>

                    <select
                      value={accessFilterRole}
                      onChange={(e) => setAccessFilterRole(e.target.value as any)}
                      className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground outline-none"
                    >
                      <option value="all">All Roles</option>
                      <option value="admin">Admins Only</option>
                      <option value="agent">Support Agents Only</option>
                      <option value="manager">Managers Only</option>
                    </select>
                  </div>
                </div>

                <div className="border border-border rounded-xl overflow-hidden bg-card shadow-2xs divide-y divide-border">
                  <div className="bg-muted/40 p-3 grid grid-cols-12 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <div className="col-span-5 sm:col-span-4">User / Agent Name</div>
                    <div className="col-span-3 sm:col-span-3">System Role &amp; Team</div>
                    <div className="col-span-2 sm:col-span-2 text-center">Access Status</div>
                    <div className="col-span-2 sm:col-span-3 text-right">Permission Level</div>
                  </div>

                  {granularRoster
                    .filter((subject) => {
                      const matchesQuery =
                        subject.name.toLowerCase().includes(accessSearchQuery.toLowerCase()) ||
                        subject.email.toLowerCase().includes(accessSearchQuery.toLowerCase()) ||
                        subject.department.toLowerCase().includes(accessSearchQuery.toLowerCase());
                      const matchesCategory =
                        accessFilterRole === 'all' || subject.category === accessFilterRole;
                      return matchesQuery && matchesCategory;
                    })
                    .map((subject) => {
                      const isAllowed = (selectedAction.allowedAgents || []).includes(subject.id) ||
                        (subject.category === 'admin' && selectedAction.allowAdmins !== false) ||
                        (subject.category === 'agent' && selectedAction.allowAgents !== false) ||
                        (subject.category === 'manager' && selectedAction.allowManagers !== false);

                      const acConfig = (selectedAction.accessControl || []).find(
                        (ac) => ac.targetName === subject.name || ac.targetName === subject.email
                      );
                      const currentLevel = acConfig?.level || (subject.category === 'admin' ? 'Owner' : 'Execute Only');

                      return (
                        <div
                          key={subject.id}
                          className="p-3 grid grid-cols-12 items-center hover:bg-accent/30 transition-colors"
                        >
                          {/* Name & Avatar */}
                          <div className="col-span-5 sm:col-span-4 flex items-center gap-2.5">
                            <div className="size-7 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center font-semibold text-xs text-primary shrink-0">
                              {subject.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                            </div>
                            <div className="overflow-hidden">
                              <span className="font-semibold text-xs text-foreground block truncate">{subject.name}</span>
                              <span className="text-xs text-muted-foreground font-mono truncate block">{subject.email}</span>
                            </div>
                          </div>

                          {/* Role & Dept */}
                          <div className="col-span-3 sm:col-span-3 space-y-0.5">
                            <span className={cn(
                              "text-xs font-semibold px-2 py-0.5 rounded-full inline-block",
                              subject.category === 'admin'
                                ? "bg-primary/10 text-primary border border-primary/20"
                                : subject.category === 'manager'
                                ? "bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300"
                                : "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300"
                            )}>
                              {subject.role}
                            </span>
                            <span className="text-xs text-muted-foreground block truncate">{subject.department}</span>
                          </div>

                          {/* Access Checkbox / Toggle */}
                          <div className="col-span-2 sm:col-span-2 text-center">
                            {isEditMode ? (
                              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isAllowed}
                                  onChange={() => handleToggleGranularAccess(subject)}
                                  className="size-4 rounded border-border text-primary focus:ring-primary"
                                />
                                <span className="text-xs font-semibold hidden sm:inline">
                                  {isAllowed ? 'Allowed' : 'Denied'}
                                </span>
                              </label>
                            ) : (
                              <span className={cn(
                                "text-xs font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1",
                                isAllowed
                                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                                  : "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300"
                              )}>
                                {isAllowed ? <Check className="size-2.5" /> : <X className="size-2.5" />}
                                {isAllowed ? 'Allowed' : 'Denied'}
                              </span>
                            )}
                          </div>

                          {/* Permission Level Selector */}
                          <div className="col-span-2 sm:col-span-3 flex justify-end">
                            {isEditMode ? (
                              <select
                                value={currentLevel}
                                disabled={!isAllowed}
                                onChange={(e) => handleChangePermissionLevel(subject, e.target.value as any)}
                                className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground outline-none disabled:opacity-40"
                              >
                                <option value="Execute Only">Execute Only (Toolbar)</option>
                                <option value="Read and Modify">Read and Modify</option>
                                <option value="Owner">Owner (Full Admin)</option>
                              </select>
                            ) : (
                              <span className="text-xs font-semibold text-foreground bg-muted/60 px-2.5 py-0.5 rounded border border-border">
                                {currentLevel}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          <div className="sticky -bottom-5 -mx-5 mt-auto flex items-center justify-between gap-2 border-t border-border bg-card px-5 py-4">
            <button type="button" onClick={handleDeleteSelectedAction} className="inline-flex h-9 items-center gap-2 rounded-lg px-3 text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive"><Trash2 className="size-4" /> Delete</button>
            <div className="flex gap-2">
              <button type="button" onClick={handleBackToList} className="h-9 rounded-lg border border-border bg-card px-4 text-sm font-medium hover:bg-secondary">Cancel</button>
              <button type="button" onClick={handleApplyAndSave} className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"><Save className="size-4" /> Save changes</button>
            </div>
          </div>
        </DialogContent>
        </Dialog>
      )}

      {/* 3. SIMULATOR / LIVE ACTION BUTTONS BAR PREVIEW */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {activeTab === 'simulator' && (
        <div className="rounded-xl border border-border bg-card p-6 shadow-xs space-y-5">
          <div className="flex items-center justify-between border-b border-border pb-3">
            <div>
              <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
                <Monitor className="size-4 text-emerald-500" /> Interactive Ticket Action Buttons Simulator
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Simulates how Action Buttons render in the top toolbar of ticket <strong className="text-foreground">TB#10042</strong>. Click any action button to test quick execution or dialog confirmations.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={cn(
                "text-xs font-semibold px-2.5 py-0.5 rounded-full uppercase",
                simTicketStatus === 'Resolved' || simTicketStatus === 'Closed'
                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                  : "bg-primary/10 text-primary"
              )}>
                Status: {simTicketStatus}
              </span>
              {simIsOnHold && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                  SLA On Hold ⏸
                </span>
              )}
            </div>
          </div>

          {/* Simulated Ticket Top Action Buttons Toolbar */}
          <div className="p-4 rounded-xl border border-border bg-muted/40 space-y-3">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
              Ticket #10042 Action Buttons Toolbar
            </span>

            <div className="flex items-center gap-2 flex-wrap">
              {actions.map((act) => (
                <button
                  key={act.id}
                  type="button"
                  onClick={() => handleSimulateClickAction(act)}
                  className="px-3 py-1.5 rounded-lg border border-border bg-card hover:bg-secondary text-foreground text-xs font-semibold shadow-2xs transition-all flex items-center gap-1.5 cursor-pointer"
                >
                  <PlayCircle className="size-3 text-muted-foreground" />
                  {act.buttonName}
                </button>
              ))}
            </div>
          </div>

          {/* Activity Logs of Executed Actions */}
          <div className="space-y-2">
            <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              <Clock className="size-3.5 text-primary" /> Live Action Audit Trail
            </span>
            <div className="p-3.5 rounded-xl border border-border bg-background space-y-1.5 font-mono text-xs max-h-48 overflow-y-auto">
              {simExecutedLogs.map((log, lIdx) => (
                <div key={lIdx} className="text-muted-foreground leading-relaxed">
                  {log}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* 4. ACTION BUTTON EXECUTION SIMULATOR DRAWER */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {activeModalAction && (
        <Dialog open onOpenChange={(open) => { if (!open) setActiveModalAction(null); }}>
          <DialogContent side="right" hideClose className="gap-0 overflow-hidden p-0" aria-describedby={undefined}>
            <DialogTitle className="sr-only">Execute action button</DialogTitle>
            <div className="p-4 border-b border-border flex items-center justify-between bg-muted/20">
              <div>
                <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
                  <PlayCircle className="size-4 text-primary" /> {activeModalAction.buttonName}
                </h3>
                <p className="text-xs text-muted-foreground">{activeModalAction.outcomeDescription}</p>
              </div>
              <button onClick={() => setActiveModalAction(null)} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent">
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleSimulateSubmitModal} className="flex-1 flex flex-col overflow-hidden text-xs">
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div className="space-y-1">
                  <label className="font-semibold text-foreground block">
                    Action Execution Note / Reason
                  </label>
                  <textarea
                    rows={3}
                    value={modalExecutionNote}
                    onChange={(e) => setModalExecutionNote(e.target.value)}
                    placeholder="Provide context or instructions for this action execution..."
                    className="w-full rounded-md border border-input bg-background p-2.5 text-xs text-foreground outline-none"
                  />
                </div>

                {/* Action Toolbar Summary */}
                <div className="p-3 rounded-lg border border-border bg-muted/40 space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground uppercase">System Use:</span>
                    <span className="font-semibold text-foreground">{activeModalAction.systemUse}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground uppercase">Target Status:</span>
                    <span className="font-semibold text-foreground">{activeModalAction.statusAfterAction}</span>
                  </div>
                  {activeModalAction.sendEmail && (
                    <div className="flex items-center justify-between text-emerald-600 dark:text-emerald-400">
                      <span className="text-xs font-semibold uppercase flex items-center gap-1">
                        <Mail className="size-3" /> Email Dispatch:
                      </span>
                      <span className="font-semibold">Enabled ({activeModalAction.defaultEmailTemplate || 'Standard Notification'})</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-border p-4 bg-card flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setActiveModalAction(null)}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 cursor-pointer"
                >
                  Apply &amp; Execute Action Button
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* 5. LINKED WORKFLOW WIRES DRAWER / MODAL */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {viewingWiresAction && (
        <Dialog open onOpenChange={(open) => { if (!open) setViewingWiresAction(null); }}>
          <DialogContent side="right" className="max-w-md gap-4 p-5" aria-describedby={undefined}>
            <div className="border-b border-border pb-3 pr-8">
              <DialogTitle className="flex items-center gap-2 text-foreground font-display text-card-title">
                <GitBranch className="size-4 text-primary" />
                <span>Workflow Transitions: {viewingWiresAction.buttonName}</span>
              </DialogTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Workflows and stage transitions that currently use this action button.
              </p>
            </div>

            <div className="space-y-3 max-h-[70vh] overflow-y-auto">
              {(!viewingWiresAction.wires || viewingWiresAction.wires.length === 0) ? (
                <div className="p-6 text-center text-muted-foreground border border-dashed border-border rounded-lg">
                  <GitBranch className="size-8 mx-auto mb-2 opacity-30 text-primary" />
                  <p className="font-semibold text-xs text-foreground">No Workflow Transitions Wired</p>
                  <p className="text-xs mt-1">This action button is not currently attached to any workflow transition wires.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {viewingWiresAction.wires.map((wire, idx) => (
                    <div key={wire.id || idx} className="p-3 rounded-lg border border-border bg-card shadow-2xs space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                          {wire.workflowName}
                        </span>
                        <span className="text-[10px] font-mono text-muted-foreground">
                          Wire #{wire.id}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs font-medium text-foreground">
                        <span className="px-2 py-0.5 rounded bg-secondary border border-border text-foreground font-semibold">
                          {wire.fromStageName}
                        </span>
                        <CornerDownRight className="size-3 text-muted-foreground" />
                        <span className="px-2 py-0.5 rounded bg-primary/10 border border-primary/20 text-primary font-semibold">
                          {wire.toStageName}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-border pt-3 flex justify-end">
              <button
                type="button"
                onClick={() => setViewingWiresAction(null)}
                className="h-8 rounded-lg border border-border bg-card px-4 text-xs font-semibold hover:bg-secondary cursor-pointer"
              >
                Close
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
